import { encryptSecret, importKey } from "@/features/cryptography";
import supabase from "@/features/supabase";
import { supabaseApi, useGetCurrentUserQuery, useGetPendingUsersForOrgKeyExchangeQuery, useSetOrgSecretsForPendingUsersMutation } from "@/store/services/supabase";
import { arrayBufferToString } from "@/utils/crypto";
import { useKeys } from "@/utils/hooks/useKeys";
import { ERROR_TYPES } from "@/utils/hooks/useKeys/constants";
import { getOrgSecretFromDatabase } from "@/utils/hooks/useKeys/helper";
import { CloseOutlined } from "@ant-design/icons";
import { useInterval } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { RealtimeChannel } from "@supabase/supabase-js";
import { Button, message, Modal } from "antd";
import { throttle } from "lodash/fp";
import { PropsWithChildren, useEffect, useState } from "react";
type PendingMembers = (typeof supabaseApi.endpoints.getPendingUsersForOrgKeyExchange)["Types"]["ResultType"];
type MemberWithEncryptedOrgSecret = {
  user_id: string;
  group_id: string;
  email: string;
  org_secret_enc: string;
};
export const OrgKeyExchange = ({
  children
}: PropsWithChildren) => {
  const [keys] = useKeys();
  const {
    data: members,
    refetch
  } = useGetPendingUsersForOrgKeyExchangeQuery();
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const [setOrgSecret] = useSetOrgSecretsForPendingUsersMutation();
  const [channel, setChannel] = useState<RealtimeChannel | undefined>(undefined);
  const [messageApi, messageContext] = message.useMessage();
  const [modal, modalContext] = Modal.useModal();
  const handleModalOpen = (members: any[]) => {
    modal.info({
      width: 500,
      content: <div>
          Encryption keys successfully exchanged with the following member{members.length > 1 ? "s" : ""} of your
          organisation. No further action required.
          <div className="list-inside list-disc">
            {members.map(m => <div key={m.user_id} className="ml-4">• {m.email}</div>)}
          </div>
        </div>
    });
  };
  const handleOrgKeyExchange = throttle(100, async () => {
    if (!keys.user || !members?.length) return;
    console.log("[KeyExchange] Initiating Key Exchange...");
    const {
      data: secret,
      error: getOrgSecretError
    } = await getOrgSecretFromDatabase({
      privateKey: keys.user.keyPair.privateKey
    });
    if (!secret || getOrgSecretError) {
      if (getOrgSecretError) {
        messageApi.destroy();
        messageApi.error({
          key: `ERROR-${new Date().getTime()}`,
          duration: 30,
          content: getOrgSecretError.message
        });
        console.error(`[KeyExchange] Fetch OrgSecret Error: ${getOrgSecretError.type} ${getOrgSecretError.message}`);
        if (getOrgSecretError?.type === ERROR_TYPES.INVALID_KEY_OR_SECRET) {
          Sentry.captureException(`handleOrgKeyExchange: getOrgSecretFromDatabase`, {
            tags: {
              section: "Encryption keys decryption"
            },
            extra: {
              userId: currentUser?.id,
              orgId: currentUser?.org_id,
              error: getOrgSecretError
            }
          });
        }
      } else {
        console.error("[KeyExchange] Missing OrgSecret but no error provided");
      }
      return;
    }
    console.log(`[KeyExchange] Pending Members: ${members?.length}`);
    const withEncryptedSecrets = await encryptOrgSecretForMembers(secret, members);
    if (!withEncryptedSecrets.length) return;
    try {
      await setOrgSecret(withEncryptedSecrets).unwrap();
      console.log(`[KeyExchange] Successfully shared OrgSecret with ${withEncryptedSecrets.length} members`);
      showKeyExchangeSuccess(withEncryptedSecrets);
    } catch (err) {
      const errorMsg = (err as {
        data?: {
          message?: string;
        };
      })?.data?.message || "Failed to set OrgSecret";
      messageApi.destroy();
      messageApi.error({
        key: `ERROR-${new Date().getTime()}`,
        duration: 30,
        content: errorMsg
      });
      console.error(`[KeyExchange] ${errorMsg}`);
      return;
    }
  });
  const encryptOrgSecretForMembers = async (secret: string, members: PendingMembers): Promise<MemberWithEncryptedOrgSecret[]> => {
    const results = await Promise.all(members.map(async user => {
      try {
        const encryptedSecret = await encryptSecret({
          publicKey: await importKey(user.public_key, ["encrypt"]),
          secret
        });
        return {
          user_id: user.id,
          group_id: user.group_id,
          email: user.email,
          org_secret_enc: arrayBufferToString(encryptedSecret)
        };
      } catch {
        console.error("[KeyExchange] Error adding OrgSecret to: ", user.email);
        return null;
      }
    }));
    return results.filter((r): r is MemberWithEncryptedOrgSecret => r !== null && !!r.org_secret_enc);
  };
  const showKeyExchangeSuccess = (withEncryptedSecrets: MemberWithEncryptedOrgSecret[]) => {
    const MESSAGE_ID = `MSG-${new Date().getTime()}`;
    messageApi.success({
      key: MESSAGE_ID,
      duration: 30,
      content: <div className="flex items-center">
          <span className="pr-6">
            Encryption keys exchanged with {withEncryptedSecrets.length} member
            {withEncryptedSecrets.length > 1 ? "s" : ""}.
          </span>
          <Button type="text" size="small" onClick={() => {
          handleModalOpen(withEncryptedSecrets);
          messageApi.destroy(MESSAGE_ID);
        }}>
            Details
          </Button>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => messageApi.destroy(MESSAGE_ID)} />
        </div>
    });
  };
  useEffect(() => {
    handleOrgKeyExchange();
  }, [members, keys]);

  // Check for channel existing every three seconds.
  const interval = useInterval(() => {
    if (channel) {
      return;
    }
    console.log("OKE: RESUBSCRIBING");
    subscribe();
  }, 3000);

  /** Subscribes to the Supabase channel, if no subscription exists. */
  const subscribe = () => {
    if (!interval.active) {
      console.log("ACTIVATE INTERVAL");
      setTimeout(interval.start, 1000);
    }
    if (channel) {
      console.error("Can't subscribe when channel is already connected.");
      return;
    }
    console.log("OKE: SUBSCRIBING");
    const newChannel = supabase.channel("user_update").on("postgres_changes", {
      event: "UPDATE",
      schema: "public",
      table: "users"
    }, refetch).subscribe(async status => {
      if (!channel) {
        return;
      }
      console.log("OKE: EVENT - " + status);
      switch (status) {
        case "CHANNEL_ERROR":
        case "TIMED_OUT":
          await supabase.removeChannel(channel);
          setChannel(undefined);
          break;
        case "CLOSED":
          setChannel(undefined);
          break;
      }
    });
    setChannel(newChannel);
  };
  useEffect(() => {
    subscribe();
    return () => {
      console.log("OKE: DISMOUNTING");
      interval.stop();
      if (channel) {
        supabase.removeChannel(channel);
        setChannel(undefined);
      }
    };
  }, []);
  return <>
      {messageContext}
      {modalContext}

      {children}
    </>;
};